import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import {
  CHECK_IN_API,
  CHECK_OUT_API,
  GET_GROUP_MEMBERS_API,
  GET_GUEST_INFO_API,
  TAG_OPTIONS_API,
} from "../../Constant/api";
import CheckInSliderStep1 from "./CheckInSliderStep1";
import CheckInSliderStep2 from "./CheckInSliderStep2";
import { useToast } from "../Toast";
import { motion, AnimatePresence } from "framer-motion";

const CheckInSlider = ({
  guestList,
  guestListRefresh,
  setIsOpen,
  guestUuid,
  inputRef,
}) => {
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [guestInfo, setGuestInfo] = useState({});
  const [groupMembers, setGroupMembers] = useState([]);
  const [tagList, setTagList] = useState([]);
  const [selectedTags, setSelectedTags] = useState([]);

  const { toast } = useToast();

  const handleSearch = (e) => {
    setSearchTerm(e.target.value);
  };

  const filteredGuestList = guestList.filter((guest) => {
    const search = searchTerm.toLowerCase();

    const matchesSearch =
      guest.name?.toLowerCase().includes(search) ||
      guest.group_name?.toLowerCase().includes(search) ||
      guest.email?.toLowerCase().includes(search) ||
      guest.contact?.toLowerCase().includes(search);

    const matchesTags =
      selectedTags.length === 0 ||
      guest.tags?.some((tag) => selectedTags.includes(tag.id));

    return matchesSearch && matchesTags;
  });

  const fetchTagList = useCallback(() => {
    setLoading(true);
    axios
      .get(TAG_OPTIONS_API, {
        headers: {
          Authorization: `Bearer ${sessionStorage.getItem("bearer_token")}`,
        },
        params: { eventId: sessionStorage.getItem("event_id") },
      })
      .then((response) => {
        setTagList(response.data.data);
        setLoading(false);
      })
      .catch((error) => {
        console.error(error);
        toast("Failed to fetch tag list", "error");
        setLoading(false);
      });
  }, [toast]);

  const fetchGroupMembers = useCallback((guestId) => {
    setLoading(true);
    axios
      .get(GET_GROUP_MEMBERS_API, {
        headers: {
          Authorization: `Bearer ${sessionStorage.getItem("bearer_token")}`,
        },
        params: {
          guestId: guestId,
        },
      })
      .then((response) => {
        setGroupMembers(response.data.data);
        setLoading(false);
      })
      .catch((error) => {
        console.error(error);
        toast("Failed to fetch group members", "error");
        setLoading(false);
      });
  }, [toast]);

  const fetchGuestInfo = useCallback((guestUuid) => {
    setLoading(true);
    axios
      .get(GET_GUEST_INFO_API, {
        headers: {
          Authorization: `Bearer ${sessionStorage.getItem("bearer_token")}`,
        },
        params: {
          guestUuid: guestUuid,
          eventId: sessionStorage.getItem("event_id"),
        },
      })
      .then((response) => {
        setGuestInfo(response.data.data);
        fetchGroupMembers(response.data.data.id);
        setStep(2);
        setLoading(false);
      })
      .catch((error) => {
        toast(error.response.data.message, "error");
        setIsOpen(false);
        setStep(1);
        console.error(error);
        setLoading(false);
      });
  }, [fetchGroupMembers, setIsOpen, toast]);

  const handleRecordFetch = useCallback((guest_id) => {
    fetchGuestInfo(guest_id);
  }, [fetchGuestInfo]);

  const handleCheckIn = ({ id, type = "single" } = {}) => {
    setLoading(true);
    let guestIds = [];

    if (type === "single" && id) {
      guestIds.push(id);
    } else if (type === "all") {
      guestIds = groupMembers.map((member) => member.id);
      guestIds.push(guestInfo.id);
    }

    axios
      .put(CHECK_IN_API, guestIds, {
        headers: {
          Authorization: `Bearer ${sessionStorage.getItem("bearer_token")}`,
        },
      })
      .then((response) => {
        setLoading(false);
        toast("Checked in successfully", "success");
        guestListRefresh();
        handleRecordFetch(guestInfo.uuid);
      })
      .catch((error) => {
        setLoading(false);
        console.error(error);
        toast("Failed to check in", "error");
      });
  };

  const handleCheckOut = ({ id, type = "single" } = {}) => {
    setLoading(true);
    let guestIds = [];

    if (type === "single" && id) {
      guestIds.push(id);
    } else if (type === "all") {
      guestIds = groupMembers.map((member) => member.id);
      guestIds.push(guestInfo.id);
    }

    axios
      .put(CHECK_OUT_API, guestIds, {
        headers: {
          Authorization: `Bearer ${sessionStorage.getItem("bearer_token")}`,
        },
      })
      .then((response) => {
        setLoading(false);
        toast("Checked out successfully", "success");
        guestListRefresh();
        handleRecordFetch(guestInfo.uuid);
      })
      .catch((error) => {
        setLoading(false);
        console.error(error);
        toast("Failed to check out", "error");
      });
  };

  useEffect(() => {
    if (guestUuid) {
      fetchGuestInfo(guestUuid);
    }
    fetchTagList();
  }, [guestUuid, fetchGuestInfo, fetchTagList]);

  const handleOnChange = (e) => {
    setGuestInfo({ ...guestInfo, [e.target.name]: e.target.value });
  };

  return (
    <div className="fixed flex w-full h-screen bg-black bg-opacity-60 z-10 text-black top-0 left-0">
      <div className="w-full h-screen" onClick={() => setIsOpen(false)} />
      <AnimatePresence mode="wait">
        {step === 1 ? (
          <motion.div
            key="step1"
            initial={{ x: "100%" }}
            animate={{ x: 0 }}
            exit={{ x: "100%" }}
            transition={{ duration: 0.3, ease: "easeInOut" }}
            className="absolute right-0 top-0 w-full md:w-[500px] h-full bg-white shadow-lg"
          >
            <CheckInSliderStep1
              loading={loading}
              searchTerm={searchTerm}
              handleSearch={handleSearch}
              setIsOpen={setIsOpen}
              filteredGuestList={filteredGuestList}
              handleRecordClick={(guest_id) => handleRecordFetch(guest_id)}
              tagList={tagList}
              selectedTags={selectedTags}
              setSelectedTags={setSelectedTags}
              inputRef={inputRef}
            />
          </motion.div>
        ) : (
          step === 2 && (
            <motion.div
              key="step2"
              initial={{ x: "100%" }}
              animate={{ x: 0 }}
              exit={{ x: "100%" }}
              transition={{ duration: 0.3, ease: "easeInOut" }}
              className="absolute right-0 top-0 w-full md:w-[500px] h-full bg-white shadow-lg"
            >
              <CheckInSliderStep2
                guestInfo={guestInfo}
                handleOnChange={handleOnChange}
                groupMembers={groupMembers}
                loading={loading}
                setIsOpen={setIsOpen}
                setStep={setStep}
                handleCheckIn={handleCheckIn}
                handleCheckOut={handleCheckOut}
                handleRecordClick={(guest_id) => handleRecordFetch(guest_id)}
                onSelectTableGuest={(guestUuid) => fetchGuestInfo(guestUuid)}
              />
            </motion.div>
          )
        )}
      </AnimatePresence>
    </div>
  );
};

export default CheckInSlider;
